import React, { useState, useEffect, useRef } from 'react';
import './HeaderProfile.scss';
import { FaUser } from 'react-icons/fa';
import NotificationCenter from './components/NotificationCenter/NotificationCenter';
import { clearEntireCache } from '../../../../../../crud/cache/cachePublicMethods';
import { useAuth } from '../../../../../../components/AuthManagement/AuthContext';

const HeaderProfile: React.FC = () => {
  const [username, setUsername] = useState<string>('Username');
  const [email, setEmail] = useState<string>('Email@example.com');
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { forceLogout } = useAuth();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 769);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const [emailLength, setEmailLength] = useState<number>(0);

  useEffect(() => {
    setEmailLength(email.length);
  }, [email]);

  useEffect(() => {
    const updateUserInfo = () => {
      const user = localStorage.getItem('user');
      if (user) {
        try {
          const userObj = JSON.parse(user);
          const full_name = `${userObj.name ? userObj.name : userObj.username} ${userObj.last_name}`;
          setUsername(full_name);
          setEmail(userObj.email);
        } catch (error) {
          setUsername('Username');
          setEmail('Email@example.com');
        }
      } else {
        setUsername('Username');
        setEmail('Email@example.com');
      }
    };

    // Initial load
    updateUserInfo();

    // Listen for storage changes (from other tabs/windows)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user') {
        updateUserInfo();
      }
    };

    // Listen for same-window user data updates
    const handleUserDataUpdate = () => {
      updateUserInfo();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userDataUpdated', handleUserDataUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userDataUpdated', handleUserDataUpdate);
    };
  }, []);

  /**
   * Returns exactly two initials.
   * - If there's only one name, use the first two letters (e.g. "Luis" → "LU").
   * - If there are multiple names, take the first letter of the first and last names
   *   (e.g. "Dietmar Krause" → "DK", "Dietmar Hans Krause" → "DK").
   */
  const getTwoInitials = (fullName: string): string => {
    if (!fullName) return '';
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    } else {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
  };

  const handleLogout = async () => {
    try {
      // Clear app cache (IndexedDB) to avoid stale data after logout
      await clearEntireCache();
      
      // Use AuthProvider's coordinated logout
      await forceLogout();
      
      // Redirect to login
      window.location.href = '/login';
    } catch (error) {
      console.error('Error during logout:', error);
      // Fallback: force logout anyway
      localStorage.clear();
      localStorage.setItem('loggedOut', 'true');
      window.location.href = '/login';
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleMenuItemClick = () => {
    setIsDropdownOpen(false);
  };

  return (
    <div className="profile-container">
      {/* The NotificationCenter with a larger bell icon (shown in step 2 below) */}
      <NotificationCenter />

      <div className="profile-dropdown-container" ref={dropdownRef}>
        <div className="profile-picture" onClick={toggleDropdown}>
          {/* Full name + icon (visible on screens > 450px) */}
          <span className="profile-fullname">
            {username} <FaUser />
          </span>

          {/* Circular initials (visible on screens < 450px) */}
          <span className="profile-initials">
            {getTwoInitials(username)}
          </span>
        </div>

        <div
          className={`profile-dropdown ${isDropdownOpen ? 'open' : ''}`}
          style={isMobile ? undefined : { right: emailLength - 10 }}
        >
          <div className="user-info">
            <p className="email-button">
              {isMobile
              ? (email.length > 30 ? email.substring(0, 30) + '...' : email)
              : email}
            </p>
          </div>
          <ul>
            <li><a href="/App/UserProfile" onClick={handleMenuItemClick}>Mi perfil</a></li>
            {/* <li><a href="/settings">Configuración</a></li> */}
            <li><a href="/#" onClick={(e) => { handleMenuItemClick(); handleLogout(); }}>Cerrar sesión</a></li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default HeaderProfile;
